import {
  ArrowBigLeftDash,
  BadgeCheck,
  Plus,
  ClipboardCheck,
} from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  getCoreRowModel,
  useReactTable,
  VisibilityState,
  Column,
} from '@tanstack/react-table'

import {
  CheckboxFilter,
  CheckboxFilterOption,
  ClearFiltersButton,
  ColumnSelectorFilter,
  DataTableNew,
  generateSkeletonData,
  isSkeletonRow,
  ListPageHeader,
  PageSizeFilter,
  SearchFilter,
  useSkeletonColumns,
} from '@/components/newRedesign/components'
import { ListPageLayout } from '@/components/newRedesign/layouts'
import { Container } from '@/interfaces/container'
import { SimpleTablePagination } from '@/components/table/SimpleTablePagination'
import { useAppSelector } from '@/store/hooks'

import { ContainerAdvancedFilters } from './filters/ContainerAdvancedFilters'
import { getColumns } from './table/Columns'
import { TableContextProvider, useTableContext } from './table/TableContext'
import {
  LogisticContainerProvider,
  useLogisticContainer,
} from './hooks/useLogisticContainer'
import ContainerWizard from './wizzardContainer/ContainerWizard'
import { CreateContainerWizard } from './dialogs'
import {
  ContainerFiltersProvider,
  useContainerFiltersContext,
} from './filters/hooks/ContainerFiltersProvider'
import {
  categoriesToOptions,
  useCategoriesQuery,
} from './hooks/useCategoriesQuery'
import { containerStatusOptions } from './filters/constants/statusOptions'
import { returnedOptions } from './filters/options/returnedOptions'

function ContainerListContent() {
  const {
    editContainer,
    showNewContainer,
    showCreateWizard,
    onCloseWizard,
    onNewContainer,
    onNewContainerSuccess,
  } = useLogisticContainer()

  const {
    filters,
    searchQuery, // Valor sin debounce para el input
    updateSearch,
    updateStatus,
    updateIsReturned,
    updateOwnerCompanyId,
    updateLogisticCompanyId,
    updateTransporterCompanyId,
    updateStorageCompanyId,
    updateCategory,
    clearAllFilters,
    hasActiveFilters,
  } = useContainerFiltersContext()

  const { data: categoriesData } = useCategoriesQuery()
  const categoryOptions = useMemo(
    () => categoriesToOptions(categoriesData?.items || []),
    [categoriesData?.items]
  )

  // Handler para crear nuevo contenedor
  const handleCreate = React.useCallback(() => {
    onNewContainer()
  }, [onNewContainer])

  const statusCheckboxOptions: CheckboxFilterOption[] = useMemo(
    () =>
      containerStatusOptions.map((option) => ({
        id: option.value,
        label: option.label,
        value: option.value,
      })),
    []
  )

  const returnedCheckboxOptions: CheckboxFilterOption[] = useMemo(
    () =>
      returnedOptions.map((option) => ({
        id: option.value,
        label: option.label,
        value: option.value,
      })),
    []
  )

  return (
    <ListPageLayout>
      {/* Header con Breadcrumb */}
      <ListPageLayout.Header
        breadcrumbIcon={<ClipboardCheck className="h-4 w-4 text-gray-400" />}
        breadcrumbItems={[{ label: 'Logística' }, { label: 'Contenedores' }]}
      />

      {/* Contenido Principal */}
      <ListPageLayout.Content>
        {/* Header de la lista con filtros - Nueva estructura Figma */}
        <TableContextProvider>
          <ContainerListHeaderWrapper
            categoryOptions={categoryOptions}
            clearAllFilters={clearAllFilters}
            filters={filters}
            handleCreate={handleCreate}
            hasActiveFilters={hasActiveFilters}
            returnedCheckboxOptions={returnedCheckboxOptions}
            searchQuery={searchQuery}
            statusCheckboxOptions={statusCheckboxOptions}
            updateCategory={updateCategory}
            updateIsReturned={updateIsReturned}
            updateLogisticCompanyId={updateLogisticCompanyId}
            updateOwnerCompanyId={updateOwnerCompanyId}
            updateSearch={updateSearch}
            updateStatus={updateStatus}
            updateStorageCompanyId={updateStorageCompanyId}
            updateTransporterCompanyId={updateTransporterCompanyId}
          />

          {/* Tabla - Usando flex-1 para ocupar espacio disponible */}
          <div className="mt-4 flex flex-1 flex-col overflow-hidden">
            <ContainerTable />
          </div>
        </TableContextProvider>
      </ListPageLayout.Content>

      {/* Wizard de contenedor (para edición) */}
      <ContainerWizard
        editContainer={editContainer}
        onClose={onCloseWizard}
        showState={showNewContainer}
      />

      {/* Nuevo wizard de creación */}
      <CreateContainerWizard
        onOpenChange={(open) => {
          if (!open) {
            onCloseWizard()
          }
        }}
        onSuccess={onNewContainerSuccess}
        open={showCreateWizard}
      />
    </ListPageLayout>
  )
}

// Componente separado que tiene acceso al TableContext
interface ContainerListHeaderProps {
  filters: ReturnType<typeof useContainerFiltersContext>['filters']
  searchQuery: string
  handleCreate: () => void
  updateSearch: (_value: string) => void
  updateStatus: (_statuses: string[]) => void
  updateIsReturned: (_value: string) => void
  statusCheckboxOptions: CheckboxFilterOption[]
  returnedCheckboxOptions: CheckboxFilterOption[]
  categoryOptions: { value: string; label: string }[]
  updateOwnerCompanyId: (_id: string) => void
  updateLogisticCompanyId: (_id: string) => void
  updateTransporterCompanyId: (_id: string) => void
  updateStorageCompanyId: (_id: string) => void
  updateCategory: (_categories: string[]) => void
  clearAllFilters: () => void
  hasActiveFilters: boolean
}

/**
 * ContainerTable - Tabla que renderiza los contenedores con DataTableNew
 */
function ContainerTable() {
  const navigate = useNavigate()
  const { business } = useAppSelector((state) => state.init)
  const {
    containerData,
    isLoading,
    isFetching,
    setColumns,
    currentPage,
    setCurrentPage,
    pageSize,
    refetch,
  } = useTableContext()

  // Obtener las columnas con la moneda del sistema
  const columns = getColumns(business?.mainCurrency || '')

  // Gestionar visibilidad de columnas con localStorage
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = localStorage.getItem('container-table-columns')
      // Si hay stored data, parsearla pero asegurarnos que totalItems no esté oculto
      if (stored) {
        const parsed = JSON.parse(stored)
        delete parsed.totalItems
        return parsed
      }
      // Columnas ocultas por defecto
      return {
        updatedAt: false,
        ownerCompany: false,
        managerCompany: false,
        importerCompany: false,
        transporterCompany: false,
        storageCompany: false,
        // totalItems (elementos) está visible por defecto - NO está en esta lista
      }
    }
  )

  const skeletonData = useMemo(() => {
    if (!isLoading && !isFetching) return []
    return generateSkeletonData<Container>(20)
  }, [isLoading, isFetching])

  const data =
    isLoading || isFetching ? skeletonData : containerData?.items || []

  // Columnas con skeleton durante carga
  const tableColumns = useSkeletonColumns(columns, isLoading || isFetching)

  // Crear tabla con TanStack
  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === 'function' ? updater(columnVisibility) : updater
      setColumnVisibility(newVisibility)
      localStorage.setItem(
        'container-table-columns',
        JSON.stringify(newVisibility)
      )
    },
    state: {
      columnVisibility,
    },
  })

  // Exponer columnas al contexto para el selector
  // Se ejecuta cada vez que cambia columnVisibility para asegurar sincronización
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allColumns = table.getAllColumns() as any
    setColumns(allColumns)
  }, [table, setColumns, columnVisibility])

  // Handler para click en fila
  const handleRowClick = (container: Container) => {
    // No navegar si es skeleton o está cargando
    if (isLoading || isFetching || isSkeletonRow(container)) return

    navigate(`/logistic/containers/${container.id}`)
  }

  return (
    <div className="flex h-full flex-col">
      {/* Tabla - flex-1 para ocupar el espacio disponible */}
      <div className="relative flex-1 overflow-hidden">
        {/* Indicador de carga durante búsqueda/filtrado */}
        {isFetching && !isLoading && (
          <div className="absolute left-0 right-0 top-0 z-10 h-0.5 overflow-hidden bg-gray-100">
            <div className="h-full w-full animate-pulse bg-blue-500" />
          </div>
        )}
        <DataTableNew height="100%" onRowClick={handleRowClick} table={table} />
      </div>

      {/* Paginación - altura fija */}
      <div className="flex-shrink-0">
        <SimpleTablePagination
          currentPage={currentPage}
          isLoading={isLoading || isFetching}
          onPageChange={setCurrentPage}
          onRefresh={refetch}
          pageSize={pageSize}
          totalItems={containerData?.totalItems || 0}
          totalPages={containerData?.totalPages || 0}
        />
      </div>
    </div>
  )
}

// Wrapper component that has access to TableContext
function ContainerListHeaderWrapper(props: ContainerListHeaderProps) {
  const { pageSize, setPageSize, columns: tableColumns } = useTableContext()

  return (
    <ContainerListHeader
      {...props}
      columns={tableColumns}
      pageSize={pageSize}
      setPageSize={setPageSize}
    />
  )
}

function ContainerListHeader({
  filters,
  searchQuery,
  handleCreate,
  updateSearch,
  updateStatus,
  updateIsReturned,
  statusCheckboxOptions,
  returnedCheckboxOptions,
  categoryOptions,
  updateOwnerCompanyId,
  updateLogisticCompanyId,
  updateTransporterCompanyId,
  updateStorageCompanyId,
  updateCategory,
  clearAllFilters,
  hasActiveFilters,
  columns,
  pageSize,
  setPageSize,
}: ContainerListHeaderProps & {
  columns: Column<Container, unknown>[]
  pageSize: number
  setPageSize: (_size: number) => void
}) {
  // Crear una key basada en el estado de visibilidad de las columnas para forzar re-render
  const columnsKey = columns
    .map((col) => `${col.id}:${col.getIsVisible()}`)
    .join(',')

  return (
    <ListPageHeader
      primaryAction={{
        label: 'Contenedor',
        onClick: handleCreate,
        icon: <Plus className="h-4 w-4" />,
      }}
      search={
        <SearchFilter
          onChange={updateSearch}
          placeholder="Buscar por No. de operación o nombre..."
          value={searchQuery}
        />
      }
      secondaryActions={
        <>
          {/* Secondary buttons - Order 2 */}
          <div className="flex-none" style={{ order: 2 }}>
            <ColumnSelectorFilter columns={columns} key={columnsKey} />
          </div>

          <div className="flex-none" style={{ order: 2 }}>
            <PageSizeFilter
              onChange={setPageSize}
              options={[10, 20, 30, 40, 50]}
              pageSize={pageSize}
            />
          </div>

          {/* Filters - Order 3 */}
          <div className="flex-none" style={{ order: 3 }}>
            <CheckboxFilter
              allOptionId="all"
              icon={<BadgeCheck className="h-4 w-4" />}
              onChange={updateStatus}
              options={statusCheckboxOptions}
              title="Estado"
              value={filters.status}
            />
          </div>

          <div className="flex-none" style={{ order: 3 }}>
            <CheckboxFilter
              allOptionId="all"
              exclusive
              icon={<ArrowBigLeftDash className="h-4 w-4" />}
              onChange={(values) => updateIsReturned(values[0] || 'all')}
              options={returnedCheckboxOptions}
              title="Retornado"
              value={[filters.isReturned]}
              variant="small"
            />
          </div>

          <div className="flex-none" style={{ order: 3 }}>
            <ContainerAdvancedFilters
              category={filters.category}
              categoryOptions={categoryOptions}
              logisticCompanyId={filters.logisticCompanyId}
              ownerCompanyId={filters.ownerCompanyId}
              storageCompanyId={filters.storageCompanyId}
              transporterCompanyId={filters.transporterCompanyId}
              updateCategory={updateCategory}
              updateLogisticCompanyId={updateLogisticCompanyId}
              updateOwnerCompanyId={updateOwnerCompanyId}
              updateStorageCompanyId={updateStorageCompanyId}
              updateTransporterCompanyId={updateTransporterCompanyId}
            />
          </div>

          {/* Clear All Filters Button - Order 4 (appears at the end) */}
          <div className="flex-none" style={{ order: 4 }}>
            <ClearFiltersButton
              onClick={clearAllFilters}
              visible={hasActiveFilters}
            />
          </div>
        </>
      }
    />
  )
}

export default function ContainerList() {
  return (
    <LogisticContainerProvider>
      <ContainerFiltersProvider>
        <ContainerListContent />
      </ContainerFiltersProvider>
    </LogisticContainerProvider>
  )
}
